import { Pipe, PipeTransform } from '@angular/core';

@Pipe({
  name: 'htmlTruncatePreserve',
  standalone: false,
})
export class HtmlTruncatePreservePipe implements PipeTransform {
  transform(value: string, maxLength: number = 400, suffix: string = '...', showAsLink: boolean = false, linkId?: string): string {
    if (!value) {
      return '';
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = value;

    const textContent = tempDiv.textContent || tempDiv.innerText || '';

    if (textContent.length <= maxLength) {
      return value;
    }

    // Truncate while preserving HTML structure
    let truncatedHtml = '';
    let textLength = 0;
    const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);

    let node;
    const openTags: string[] = [];

    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;
        const remainingLength = maxLength - textLength;

        if (remainingLength <= 0) {
          break;
        }

        let nodeText = textNode.textContent || '';
        if (textLength + nodeText.length > maxLength) {
          nodeText = nodeText.substring(0, remainingLength);
          const lastSpace = nodeText.lastIndexOf(' ');
          nodeText = lastSpace > 0 ? nodeText.substring(0, lastSpace) : nodeText;
        }

        truncatedHtml += nodeText;
        textLength += nodeText.length;

        if (textLength >= maxLength) {
          break;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();

        if (this.isOpeningTag(walker, element)) {
          truncatedHtml += `<${tagName}${this.getAttributes(element)}>`;
          if (!this.isSelfClosingTag(tagName)) {
            openTags.push(tagName);
          }
        }
      }
    }

    // Close any remaining open tags
    while (openTags.length > 0) {
      const tagName = openTags.pop();
      truncatedHtml += `</${tagName}>`;
    }

    // Add suffix as a link if requested
    if (showAsLink && linkId) {
      return truncatedHtml + `... <a href="#" class="read-more-link" data-id="${linkId}" onclick="return false;">${suffix}</a>`;
    }

    return truncatedHtml + suffix;
  }

  private isOpeningTag(walker: TreeWalker, element: Element): boolean {
    // This is a simplified approach - in practice, you might need more sophisticated logic
    const nextNode = walker.nextSibling();
    if (nextNode && nextNode.nodeType === Node.ELEMENT_NODE) {
      const nextElement = nextNode as Element;
      return nextElement.tagName.toLowerCase() === element.tagName.toLowerCase();
    }
    return true; // Assume it's an opening tag if no sibling follows
  }

  private isSelfClosingTag(tagName: string): boolean {
    const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'source', 'track', 'wbr'];
    return selfClosingTags.includes(tagName);
  }

  private getAttributes(element: Element): string {
    let attrs = '';
    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      attrs += ` ${attr.name}="${attr.value}"`;
    }
    return attrs;
  }
}
